import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAppContext } from '../context/AppContext'
import { T } from './Translation'
import { useLanguage } from '../context/LanguageContext'
import { translations } from '../translations/translations'

import '../styles/EditSettings.css'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000'

const toLocalDatetime = (isoString) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  return date.toISOString().slice(0, 16)
}

const hasConsecutiveWhitespace = (value) => / {2,}|\r?\n\r?\n/.test(value)

const NAV_ITEMS = [
  { id: 'personal',  icon: '👤', labelKey: 'step1UserDetails' },
  { id: 'event',     icon: '📅', labelKey: 'step3EventDetails' },
  { id: 'invitation',icon: '📸', labelKey: 'uploadInvitation' },
  { id: 'messages',  icon: '💬', labelKey: 'rsvpMessages' },
  { id: 'transport', icon: '🚌', labelKey: 'transportSettings' },
  { id: 'password',  icon: '🔒', labelKey: 'changePassword' },
]

const EditSettings = () => {
  const { user } = useAppContext()
  const { language } = useLanguage()
  const navigate = useNavigate()

  // ── Form states (unchanged) ──────────────────────────────────────
  const [eventOrganizer, setEventOrganizer] = useState('')
  const [wifeFirstname, setWifeFirstname] = useState('')
  const [wifeLastname, setWifeLastname] = useState('')
  const [husbandFirstname, setHusbandFirstname] = useState('')
  const [husbandLastname, setHusbandLastname] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [eventAddress, setEventAddress] = useState('')
  const [clearEventAddress, setClearEventAddress] = useState('')
  const [invitationFile, setInvitationFile] = useState(null)
  const [currentInvitationImage, setCurrentInvitationImage] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [oldPassword, setOldPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [rsvpText, setRsvpText] = useState('')
  const [rsvpSecondText, setRsvpSecondText] = useState('')
  const [reminderText, setReminderText] = useState('')
  const [afterWeddingText, setAfterWeddingText] = useState('')
  const [organizeTransport, setOrganizeTransport] = useState(false)
  const [transportStops, setTransportStops] = useState([])
  const [messagingChannel, setMessagingChannel] = useState('')
  const [freeText, setFreeText] = useState('')
  const [lastFreeText, setLastFreeText] = useState('')
  const [eventType, setEventType] = useState('')
  const [error, setError] = useState('')
  const freeTextIsTooLong = freeText.length > 200
  const freeTextHasConsecutiveWhitespace = hasConsecutiveWhitespace(freeText)
  const whatsappFreeTextIsInvalid = messagingChannel === 'whatsapp'
    && (freeTextIsTooLong || freeTextHasConsecutiveWhitespace)

  // ── Active nav section (IntersectionObserver) ────────────────────
  const [activeSection, setActiveSection] = useState('personal')
  const sectionRefs = useRef({})

  useEffect(() => {
    if (!user) {
      navigate('/admin-login')
    } else {
      fetchAdminDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user])

  // Track which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    )
    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [messagingChannel]) // re-run when whatsapp state changes (messages section may appear/disappear)

  const scrollTo = (id) => {
    const el = sectionRefs.current[id]
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  /* ── Fetch Admin Details ──────────────────────────────────────── */
  async function fetchAdminDetails() {
    try {
      const res = await axios.get(`${BASE_URL}/admin/event-info`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      setEventOrganizer(res.data.event_organizer || '')
      setWifeFirstname(res.data.wife_firstname || '')
      setWifeLastname(res.data.wife_lastname || '')
      setHusbandFirstname(res.data.husband_firstname || '')
      setHusbandLastname(res.data.husband_lastname || '')
      setEmail(res.data.email || '')
      setPhoneNumber(res.data.phone_number || '')
      setEventDate(toLocalDatetime(res.data.event_date))
      setEventAddress(res.data.event_address || '')
      setClearEventAddress(res.data.cleaned_event_address || '')
      setRsvpText(res.data.rsvp_text || '')
      setRsvpSecondText(res.data.rsvp_second_text || '')
      setReminderText(res.data.reminder_text || '')
      setAfterWeddingText(res.data.after_wedding || '')
      setOrganizeTransport(res.data.organize_transport || false)
      setTransportStops(res.data.transport_stops || [])
      setCurrentInvitationImage(res.data.invitation_url || '')
      setMessagingChannel(res.data.messaging_channel || '')
      setFreeText(res.data.free_text || '')
      setLastFreeText(res.data.last_free_text || '')
      setEventType(res.data.event_type || '')
    } catch (err) {
      console.error('Failed to fetch admin details', err)
    }
  }

  /* ── Save ─────────────────────────────────────────────────────── */
  const handleSaveEdit = async (e) => {
    e.preventDefault()
    const formattedDate = eventDate

    if (whatsappFreeTextIsInvalid) {
      return
    }

    if (newPassword !== '') {
      if (!isValidPassword(newPassword)) {
        setError(translations[language].newPasswordValidation)
        return
      }
      if (newPassword !== confirmNewPassword) {
        setError(translations[language].passwordsDoNotMatch)
        return
      }
    }

    setError('')
    setLoading(true)
    try {
      // 1) If a new image was selected, upload it directly to S3 via presigned URL
      let newInvitationUrl = null
      if (invitationFile) {
        const ext = invitationFile.name.includes('.')
          ? '.' + invitationFile.name.split('.').pop()
          : '.jpg'
        const contentType = invitationFile.type || 'image/jpeg'

        const { data: uploadData } = await axios.get(
          `${BASE_URL}/admin/invitation-upload-url`,
          {
            params: { ext, contentType },
            headers: { Authorization: `Bearer ${user.token}` },
          }
        )

        await axios.put(uploadData.uploadUrl, invitationFile, {
          headers: { 'Content-Type': contentType },
        })

        newInvitationUrl = uploadData.s3ObjectUrl
      }

      // 2) Save all settings as JSON
      const payload = {
        event_organizer: eventOrganizer,
        wife_firstname: wifeFirstname,
        wife_lastname: wifeLastname,
        husband_firstname: husbandFirstname,
        husband_lastname: husbandLastname,
        email,
        phone_number: phoneNumber,
        event_date: formattedDate,
        event_address: eventAddress,
        new_password: newPassword,
        old_password: oldPassword,
        rsvpText,
        rsvpTextSecond: rsvpSecondText,
        reminderText,
        afterWeddingText,
        organize_transport: organizeTransport,
        transport_stops: JSON.stringify(transportStops),
        free_text: freeText,
        last_free_text: lastFreeText,
        ...(newInvitationUrl ? { invitation_url: newInvitationUrl } : {}),
      }

      await axios.put(`${BASE_URL}/admin/edit`, payload, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      })

      alert(translations[language].detailsUpdatedSuccessfully)
      fetchAdminDetails()
    } catch (err) {
      console.error('Failed to update details', err)
      alert(translations[language].failedToUpdateDetails)
    }
    setLoading(false)
  }

  const isValidPassword = (password) => {
    const strongPassRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/
    return strongPassRegex.test(password)
  }

  // Visible nav items
  const visibleNav = NAV_ITEMS.filter(
    (item) => !(item.whatsappHide && messagingChannel === 'whatsapp')
  )

  const organizers = eventType === 'חתונה'
    ? [
      [wifeFirstname, wifeLastname].filter(Boolean).join(' '),
      [husbandFirstname, husbandLastname].filter(Boolean).join(' '),
    ].filter(Boolean).join(' ו') || '___'
    : eventOrganizer || '___'
  const timeStr = eventDate ? eventDate.slice(11, 16) : '___'

  const buildWhatsAppPreview = () => {
    const hebrewDays = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']
    const hebrewMonths = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']

    let dayOfWeek = '___', dateStr = '___'
    if (eventDate) {
      const d = new Date(eventDate)
      dayOfWeek = hebrewDays[d.getDay()]
      dateStr = `${d.getDate()} ב${hebrewMonths[d.getMonth()]} ${d.getFullYear()}`
    }

    const venue = clearEventAddress || '___'
    const type = eventType || '___'
    const freeTextSection = freeText ? `\n\n${freeText}` : ''

    return `אורחים וחברים יקרים,\nהנכם מוזמנים ל${type} של ${organizers}!\nהאירוע יתקיים ביום ${dayOfWeek}, ${dateStr}, ב${venue}.\nקבלת פנים בשעה ${timeStr}.${freeTextSection}\n\nלחצ/י על הכפתורים לאישור הגעה`
  }

  const buildWhatsAppLastPreview = () => {
    const wazeLink = eventAddress
      ? `https://waze.com/ul?q=${encodeURIComponent(eventAddress)}&navigate=yes`
      : '___'
    const freeTextSection = lastFreeText ? `\n${lastFreeText}` : ''

    return `אורחים יקרים 🩵\nנרגשים לראות אתכם היום ב${eventType || '___'} של ${organizers} בשעה ${timeStr}!\nלניווט: ${wazeLink}${freeTextSection}\n🥳`
  }

  return (
    <div className="es-container">
      {/* ── Top bar ── */}
      <header className="es-topbar">
        <button className="es-topbar-back" onClick={() => navigate('/admin')}>‹</button>
        <span className="es-topbar-title"><T k="editDetails" /></span>
        <div style={{ width: 36 }} /> {/* balance the back button */}
      </header>

      <form onSubmit={handleSaveEdit}>
        <div className="es-shell">

          {/* ── Content ── */}
          <main className="es-content">

            {/* 1. Personal */}
            <section
              id="personal"
              className="es-section"
              ref={(el) => { sectionRefs.current.personal = el }}
            >
              <h2 className="es-section-title"><T k="step1UserDetails" /></h2>
              <hr className="es-divider" />

              {eventType && (eventType === 'חתונה' ? (
                <div className="es-grid-2">
                  <div className="es-field">
                    <label className="es-label"><T k="wifeFirstname" /></label>
                    <input
                      className="es-input"
                      type="text"
                      value={wifeFirstname}
                      onChange={(e) => setWifeFirstname(e.target.value)}
                    />
                  </div>
                  <div className="es-field">
                    <label className="es-label"><T k="wifeLastname" /></label>
                    <input
                      className="es-input"
                      type="text"
                      value={wifeLastname}
                      onChange={(e) => setWifeLastname(e.target.value)}
                    />
                  </div>
                  <div className="es-field">
                    <label className="es-label"><T k="husbandFirstname" /></label>
                    <input
                      className="es-input"
                      type="text"
                      value={husbandFirstname}
                      onChange={(e) => setHusbandFirstname(e.target.value)}
                    />
                  </div>
                  <div className="es-field">
                    <label className="es-label"><T k="husbandLastname" /></label>
                    <input
                      className="es-input"
                      type="text"
                      value={husbandLastname}
                      onChange={(e) => setHusbandLastname(e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <div className="es-field">
                  <label className="es-label"><T k="eventOrganizer" /></label>
                  <input
                    className="es-input"
                    type="text"
                    value={eventOrganizer}
                    onChange={(e) => setEventOrganizer(e.target.value)}
                  />
                </div>
              ))}
            </section>

            {/* 2. Event */}
            <section
              id="event"
              className="es-section"
              ref={(el) => { sectionRefs.current.event = el }}
            >
              <h2 className="es-section-title"><T k="step3EventDetails" /></h2>
              <hr className="es-divider" />

              <div className="es-grid-2">
                <div className="es-field">
                  <label className="es-label"><T k="eventDate" /></label>
                  <input
                    className="es-input"
                    type="datetime-local"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
                <div className="es-field">
                  <label className="es-label"><T k="email" /></label>
                  <input
                    className="es-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="es-field es-full">
                  <label className="es-label"><T k="eventAddress" /></label>
                  <input
                    className="es-input"
                    type="text"
                    value={eventAddress}
                    onChange={(e) => setEventAddress(e.target.value)}
                  />
                </div>
                <div className="es-field">
                  <label className="es-label"><T k="phoneNumber" /></label>
                  <input
                    className="es-input"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
              </div>
            </section>

            {/* 3. Invitation */}
            <section
              id="invitation"
              className="es-section"
              ref={(el) => { sectionRefs.current.invitation = el }}
            >
              <h2 className="es-section-title"><T k="uploadInvitation" /></h2>
              <hr className="es-divider" />

              {currentInvitationImage && !invitationFile && (
                <div className="es-upload-preview">
                  <span className="es-upload-preview-label"><T k="currentInvitation" /></span>
                  <img
                    src={currentInvitationImage}
                    alt="Current invitation"
                    className="es-upload-preview-img"
                  />
                </div>
              )}
              {invitationFile && (
                <div className="es-upload-preview">
                  <span className="es-upload-preview-label"><T k="newInvitationPreview" /></span>
                  <img
                    src={URL.createObjectURL(invitationFile)}
                    alt="New invitation preview"
                    className="es-upload-preview-img"
                  />
                </div>
              )}

              <label className="es-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (!file) return
                    if (file.size > 10 * 1024 * 1024) {
                      alert(language === 'he'
                        ? 'הקובץ גדול מדי. גודל מקסימלי הוא 10MB.'
                        : 'File is too large. Maximum size is 10MB.')
                      e.target.value = ''
                      return
                    }
                    setInvitationFile(file)
                  }}
                  className="es-upload-input"
                />
                <div className="es-upload-zone">
                  <span className="es-upload-zone-icon">📸</span>
                  <span className="es-upload-zone-primary">
                    {invitationFile
                      ? invitationFile.name
                      : currentInvitationImage
                        ? translations[language].replaceInvitationTitle || 'Replace invitation image'
                        : translations[language].uploadInvitationTitle}
                  </span>
                  <span className="es-upload-zone-secondary">
                    {invitationFile ? '' : translations[language].uploadInvitationDescription}
                  </span>
                </div>
              </label>
            </section>

            {/* 4. Messages */}
            <section
              id="messages"
              className="es-section"
              ref={(el) => { sectionRefs.current.messages = el }}
            >
              <h2 className="es-section-title"><T k="rsvpMessages" /></h2>
              <hr className="es-divider" />

              {messagingChannel === 'whatsapp' ? (
                <div className="es-field">
                  <label className="es-label" htmlFor="whatsapp-free-text">
                    <T k="whatsappFreeTextLabel" />
                  </label>
                  <textarea
                    id="whatsapp-free-text"
                    className="es-textarea"
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    aria-invalid={freeTextIsTooLong || freeTextHasConsecutiveWhitespace}
                    aria-describedby="whatsapp-free-text-feedback"
                    rows={4}
                    placeholder={
                      language === 'he'
                        ? 'אתר האירוע: wedding.com | קוד לבוש: פורמלי | חניה זמינה ליד האולם'
                        : 'Event website: wedding.com | Dress code: formal | Parking available near the venue'
                    }
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    {freeTextIsTooLong ? (
                      <span id="whatsapp-free-text-feedback" style={{ color: '#e53e3e', fontSize: '0.85em', fontWeight: 500 }}>
                        {language === 'he'
                          ? `חריגה מהמגבלה — ${freeText.length}/200 תווים`
                          : `Exceeds limit — ${freeText.length}/200 characters`}
                      </span>
                    ) : freeTextHasConsecutiveWhitespace ? (
                      <span id="whatsapp-free-text-feedback" style={{ color: '#e53e3e', fontSize: '0.85em', fontWeight: 500 }}>
                        {translations[language].whatsappFreeTextWhitespaceError}
                      </span>
                    ) : (
                      <span id="whatsapp-free-text-feedback" style={{ fontSize: '0.85em', opacity: 0.7 }}>
                        {translations[language].whatsappFreeTextHelp}
                      </span>
                    )}
                    <span style={{ fontSize: '0.82em', opacity: freeTextIsTooLong ? 1 : 0.5, color: freeTextIsTooLong ? '#e53e3e' : 'inherit', marginLeft: 8, whiteSpace: 'nowrap' }}>
                      {freeText.length}/200
                    </span>
                  </div>

                  {/* WhatsApp message preview */}
                  <div style={{ marginTop: 20 }}>
                    <p style={{ fontSize: '0.82em', opacity: 0.6, marginBottom: 8, textAlign: 'right' }}>
                      {language === 'he' ? 'תצוגה מקדימה של ההודעה:' : 'Message preview:'}
                    </p>
                    {/* Chat window */}
                    <div style={{
                      maxWidth: 300,
                      margin: '0 auto',
                      background: '#efeae2',
                      borderRadius: 14,
                      padding: '12px 10px 10px',
                      direction: 'ltr',
                    }}>
                      {/* Card: image + text + buttons, all one white unit */}
                      <div style={{
                        maxWidth: '88%',
                        marginLeft: 6,
                        borderRadius: '10px 10px 10px 10px',
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                        fontFamily: 'system-ui, sans-serif',
                      }}>
                        {/* Image header */}
                        {currentInvitationImage ? (
                          <img
                            src={currentInvitationImage}
                            alt="invitation"
                            style={{ width: '100%', display: 'block', maxHeight: 380, objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            background: '#cfd8dc',
                            height: 90,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            color: '#78909c',
                            fontSize: '0.75em',
                          }}>
                            <span style={{ fontSize: '1.6em' }}>🖼️</span>
                            {language === 'he' ? 'תמונת הזמנה' : 'Invitation image'}
                          </div>
                        )}

                        {/* Text body */}
                        <div style={{ background: '#fff', padding: '8px 10px 5px', direction: 'rtl' }}>
                          <p style={{
                            margin: 0,
                            fontSize: '0.87em',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                            color: '#111b21',
                          }}>
                            {buildWhatsAppPreview()}
                          </p>
                          <div style={{ textAlign: 'left', marginTop: 3 }}>
                            <span style={{ fontSize: '0.7em', color: '#8696a0' }}>12:00</span>
                          </div>
                        </div>

                        {/* Quick reply buttons */}
                        {[
                          language === 'he'? 'כן אני אגיע!' : 'Attending',
                          language === 'he' ? 'לצערי לא' : 'Not attending',
                          language === 'he' ? 'עדיין לא יודע/ת' : 'Not attending',
                        ].map((label) => (
                          <div key={label} style={{
                            background: '#fff',
                            borderTop: '1px solid #e9edef',
                            padding: '9px 10px',
                            textAlign: 'center',
                            color: '#3f8e4d',
                            fontSize: '0.84em',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 5,
                            direction: 'rtl',
                          }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3f8e4din" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <polyline points="9 17 4 12 9 7"/><line x1="20" y1="12" x2="4" y2="12"/>
                            </svg>
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="es-field es-last-message">
                    <label className="es-label" htmlFor="whatsapp-last-free-text">
                      <T k="whatsappLastFreeTextLabel" />
                    </label>
                    <textarea
                      id="whatsapp-last-free-text"
                      className="es-textarea"
                      value={lastFreeText}
                      onChange={(e) => setLastFreeText(e.target.value)}
                      aria-describedby="whatsapp-last-free-text-help"
                      rows={3}
                      dir="auto"
                      placeholder={translations[language].whatsappLastFreeTextPlaceholder}
                    />
                    <p id="whatsapp-last-free-text-help" className="es-message-help">
                      <T k="whatsappLastFreeTextHelp" />
                    </p>
                    <p id="whatsapp-last-message-preview-label" className="es-message-preview-label">
                      <T k="whatsappLastMessagePreview" />
                    </p>
                    <div
                      className="es-message-preview"
                      role="region"
                      aria-labelledby="whatsapp-last-message-preview-label"
                    >
                      <p className="es-message-preview-text" dir="rtl">
                        {buildWhatsAppLastPreview()}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="es-field">
                    <label className="es-label"><T k="firstRsvpMessage" /></label>
                    <textarea
                      className="es-textarea"
                      value={rsvpText}
                      onChange={(e) => setRsvpText(e.target.value)}
                      rows={3}
                      placeholder={translations[language].firstRsvpPlaceholder}
                    />
                  </div>
                  <div className="es-field">
                    <label className="es-label"><T k="reminderRsvpMessage" /></label>
                    <textarea
                      className="es-textarea"
                      value={rsvpSecondText}
                      onChange={(e) => setRsvpSecondText(e.target.value)}
                      rows={3}
                      placeholder={translations[language].reminderRsvpPlaceholder}
                    />
                  </div>
                  <div className="es-field">
                    <label className="es-label"><T k="generalReminderText" /></label>
                    <textarea
                      className="es-textarea"
                      value={reminderText}
                      onChange={(e) => setReminderText(e.target.value)}
                      rows={3}
                      placeholder={translations[language].generalReminderPlaceholder}
                    />
                  </div>
                  <div className="es-field">
                    <label className="es-label"><T k="afterWeddingThankYou" /></label>
                    <textarea
                      className="es-textarea"
                      value={afterWeddingText}
                      onChange={(e) => setAfterWeddingText(e.target.value)}
                      rows={3}
                      placeholder={translations[language].afterWeddingPlaceholder}
                    />
                  </div>
                </div>
              )}
            </section>

            {/* 5. Transport */}
            <section
              id="transport"
              className="es-section"
              ref={(el) => { sectionRefs.current.transport = el }}
            >
              <h2 className="es-section-title"><T k="transportSettings" /></h2>
              <hr className="es-divider" />

              <div className="es-transport-row">
                <span className="es-transport-row-title"><T k="organizeTransportForGuests" /></span>
                <label className="es-switch">
                  <input
                    type="checkbox"
                    checked={organizeTransport}
                    onChange={(e) => setOrganizeTransport(e.target.checked)}
                  />
                  <span className="es-switch-track" />
                </label>
              </div>

              {organizeTransport && (
                <div className="es-stops-box">
                  <div className="es-stops-header">
                    <span className="es-stops-title"><T k="transportStops" /></span>
                    {transportStops.length > 0 && (
                      <span className="es-stops-count">{transportStops.length}</span>
                    )}
                  </div>

                  {transportStops.length === 0 ? (
                    <p className="es-no-stops"><T k="noTransportStopsYet" /></p>
                  ) : (
                    transportStops.map((stop, index) => (
                      <div
                        key={index}
                        className={`es-stop-item ${index === transportStops.length - 1 ? 'new-stop' : ''}`}
                      >
                        <div className="es-stop-num">{index + 1}</div>
                        <input
                          type="text"
                          value={stop}
                          onChange={(e) => {
                            const newStops = [...transportStops]
                            newStops[index] = e.target.value
                            setTransportStops(newStops)
                          }}
                          className="es-stop-input"
                          placeholder={`${translations[language].enterStopLocation} ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="es-remove-btn"
                          title={translations[language].remove}
                          onClick={() => {
                            const stopItem = document.querySelector(`.es-stop-item:nth-child(${index + 1})`)
                            if (stopItem) {
                              stopItem.classList.add('removing-stop')
                              setTimeout(() => {
                                setTransportStops(transportStops.filter((_, i) => i !== index))
                              }, 220)
                            }
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    ))
                  )}

                  <button
                    type="button"
                    className="es-add-stop-btn"
                    onClick={(e) => {
                      setTransportStops([...transportStops, ''])
                      e.target.classList.add('success-feedback')
                      setTimeout(() => e.target.classList.remove('success-feedback'), 600)
                      setTimeout(() => {
                        const inputs = document.querySelectorAll('.es-stop-input')
                        const last = inputs[inputs.length - 1]
                        if (last) {
                          last.focus()
                          last.classList.add('new-stop-highlight')
                          setTimeout(() => last.classList.remove('new-stop-highlight'), 2000)
                        }
                      }, 100)
                    }}
                  >
                    ➕ <T k="addTransportStop" />
                  </button>
                </div>
              )}
            </section>

            {/* 6. Password */}
            <section
              id="password"
              className="es-section"
              ref={(el) => { sectionRefs.current.password = el }}
            >
              <h2 className="es-section-title"><T k="changePassword" /></h2>
              <hr className="es-divider" />

              <div className="es-password-box">
                <div className="es-field">
                  <label className="es-label"><T k="oldPassword" /></label>
                  <input
                    className="es-input"
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder={translations[language].currentPasswordPlaceholder || 'Enter current password'}
                  />
                </div>
                <div className="es-field">
                  <label className="es-label"><T k="newPassword" /></label>
                  <input
                    className="es-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={translations[language].newPasswordPlaceholder}
                  />
                </div>
                <div className="es-field">
                  <label className="es-label"><T k="confirmPassword" /></label>
                  <input
                    className="es-input"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder={translations[language].confirmPasswordPlaceholder}
                  />
                </div>
                {error && <p className="es-error">{error}</p>}
              </div>
            </section>

          </main>

          {/* ── Sidebar ── */}
          <aside className="es-sidebar">
            <nav className="es-nav">
              {visibleNav.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`es-nav-item${activeSection === item.id ? ' active' : ''}`}
                  onClick={() => scrollTo(item.id)}
                >
                  <span className="es-nav-icon">{item.icon}</span>
                  <T k={item.labelKey} />
                </button>
              ))}
            </nav>

            <div className="es-sidebar-footer">
              <button type="submit" className="es-save-btn" disabled={loading || whatsappFreeTextIsInvalid}>
                {loading ? <T k="saving" /> : <T k="saveChanges" />}
              </button>
            </div>
          </aside>

        </div>

        {/* ── Mobile floating save bar ── */}
        <div className="es-mobile-save">
          <button type="submit" className="es-save-btn" disabled={loading || whatsappFreeTextIsInvalid}>
            {loading ? <T k="saving" /> : <T k="saveChanges" />}
          </button>
        </div>

      </form>
    </div>
  )
}

export default EditSettings
